/**
 * Frontier feature types (Sprint 282 Stream G; REQ-FRT-016).
 *
 * Mirror of the backend schemas in `api/src/frontier/schemas.py` (panel read
 * models). Kept local to the feature so pages/hooks share one contract.
 */

export type RoutineStatus =
  | 'draft'
  | 'active'
  | 'paused'
  | 'auto_paused'
  | 'expired'
  | 'disabled'
  | 'archived';

export type RunStatus =
  | 'queued'
  | 'running'
  | 'waiting_input'
  | 'waiting_approval'
  | 'retrying'
  | 'paused'
  | 'blocked'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'emergency_stopped'
  | 'expired';

export type RunOrigin =
  | 'schedule'
  | 'webhook'
  | 'api'
  | 'manual'
  | 'chat'
  | 'plan_step'
  | 'loop_role'
  | 'retry'
  | 'rerun';

export interface FrontierRoutine {
  id: string;
  tenant_id: string;
  name: string;
  description?: string | null;
  agent_id: string;
  objective: string;
  completion_criteria: string[];
  run_as: Record<string, unknown>;
  limits: Record<string, unknown>;
  approval_mode: string;
  input_request_policy: string;
  overlap_policy: string;
  tags: string[];
  owner_id: string;
  status: RoutineStatus;
  paused_reason?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface FrontierRunListItem {
  id: string;
  routine_id: string | null;
  routine_name?: string | null;
  origin: RunOrigin;
  status: RunStatus;
  wait_reason?: string | null;
  outcome?: string | null;
  stop_reason?: string | null;
  error_class?: string | null;
  error_code?: string | null;
  scheduled_for?: string | null;
  queued_at: string;
  started_at?: string | null;
  ended_at?: string | null;
  deadline_at?: string | null;
  steps_used: number;
  tokens_used: number;
  cost_used: number | string;
  attempt: number;
  test: boolean;
}

export interface FrontierRunList {
  items: FrontierRunListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface FrontierCheckpointItem {
  step: number;
  ledger_revision?: number | null;
  created_at: string;
}

export interface FrontierEventItem {
  id: string;
  type: string;
  created_at: string;
  data: Record<string, unknown>;
  traceparent?: string | null;
}

/** Vista Eventos — fila del feed por tenant (REQ-FRT-015/016). */
export interface FrontierEventFeedItem extends FrontierEventItem {
  run_id?: string | null;
  routine_id?: string | null;
  routine_name?: string | null;
}

export interface FrontierEventFeedFilters {
  entity?: string;
  type?: string;
  routine_id?: string;
  run_id?: string;
  errors_only?: boolean;
  limit?: number;
  offset?: number;
}

export interface FrontierEventFeed {
  items: FrontierEventFeedItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface FrontierMemoryView {
  revision: number;
  facts: Record<string, unknown>;
  assumptions: unknown[];
  open_questions: unknown[];
  artifacts: unknown[];
  updated_at: string;
}

export interface FrontierRunDetail extends FrontierRunListItem {
  attempts?: RunAttemptInfo[];
  effective_params?: {
    values?: Record<string, unknown>;
    source_trace?: Record<string, string>;
  } | null;
  rendered_prompt?: string | null;
  run_as: Record<string, unknown>;
  checkpoints: FrontierCheckpointItem[];
  memory?: FrontierMemoryView | null;
  events: FrontierEventItem[];
}

export interface FrontierErrorGroup {
  key: string;
  count: number;
  last_seen: string;
  sample_run_id: string;
  sample_routine_id?: string | null;
  sample_routine_name?: string | null;
}

export interface FrontierErrors {
  window_hours: number;
  groups: FrontierErrorGroup[];
}

export interface FrontierSummary {
  routines: Record<string, number>;
  runs: Record<string, number>;
  errors_24h: number;
  next_scheduled_at: string | null;
  generated_at: string;
}

/** Action × state matrix of the entrega 1 (REQ-FRT-016 §3). */
export const RUN_CANCELLABLE: RunStatus[] = [
  'queued',
  'running',
  'waiting_input',
  'waiting_approval',
  'retrying',
  'paused',
  'blocked',
];

export const RUN_RESUMABLE: RunStatus[] = ['blocked', 'paused'];

export const ROUTINE_ACTIVATABLE: RoutineStatus[] = ['draft', 'paused'];

export const ROUTINE_PAUSABLE: RoutineStatus[] = ['active'];

export const ROUTINE_RESUMABLE: RoutineStatus[] = ['paused', 'auto_paused'];

export const ROUTINE_ARCHIVABLE: RoutineStatus[] = [
  'draft',
  'active',
  'paused',
  'auto_paused',
  'expired',
  'disabled',
];

// ---------------------------------------------------------------- editor (ISS-641)

export interface RoutineParameterSpec {
  name: string;
  type: 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array';
  required: boolean;
  default?: unknown;
  description?: string;
  overridable?: boolean;
}

export interface RoutineTriggerSummary {
  id: string;
  type: string;
  enabled: boolean;
}

export interface RoutineConfig {
  routine_id: string;
  name: string;
  agent_id: string;
  objective: string;
  completion_criteria: string[];
  run_as: { mode: 'user' | 'service_account'; subject_id: string; attribution_user_id?: string | null };
  start_prompt_template: string;
  input_schema: RoutineParameterSpec[];
  limits: Record<string, unknown>;
  notification_config?: Record<string, unknown> | null;
  /** Disparador de programación para precargar el editor (REQ-FRT-002). */
  schedule?: ScheduleTriggerSpec | null;
  schedule_enabled?: boolean;
  triggers?: RoutineTriggerSummary[];
}

export interface RoutineCreateInput {
  name: string;
  description?: string;
  agent_id: string;
  start_prompt_template: string;
  input_schema: RoutineParameterSpec[];
  objective: string;
  completion_criteria: string[];
  run_as: { mode: 'user' | 'service_account'; subject_id: string };
  limits: Record<string, unknown>;
  approval_mode: 'pause_and_notify' | 'proposal_only';
  input_request_policy: 'auto_continue' | 'use_defaults' | 'pause_and_notify' | 'fail';
  overlap_policy: 'skip' | 'queue_one' | 'queue_all' | 'cancel_previous' | 'parallel';
  tags: string[];
}

export interface ScheduleTriggerSpec {
  rrule: string;
  timezone: string;
  dtstart?: string | null;
  end?: 'never' | 'until' | 'count';
  until?: string | null;
  count?: number | null;
  exdates?: string[];
}

export interface ScheduleTriggerInput {
  type: 'schedule' | 'webhook' | 'api';
  enabled: boolean;
  schedule?: ScheduleTriggerSpec;
}

/** Constructor de periodicidad — vista previa (REQ-FRT-002 §4). */
export interface SchedulePreviewInput {
  rrule: string;
  timezone: string;
  dtstart?: string | null;
  end?: 'never' | 'until' | 'count';
  until?: string | null;
  count?: number | null;
  exdates?: string[];
  occurrences?: number;
}

export interface SchedulePreview {
  timezone: string;
  rrule: string;
  next_occurrences: string[];
  interval_minutes?: number | null;
  exhausted: boolean;
  timezone_valid: boolean;
}

export interface LaunchRoutineInput {
  params: Record<string, unknown>;
  test: boolean;
  overrides?: Record<string, unknown>;
}

// ---------------------------------------------------------------- consents (ISS-644)

export interface FrontierConsent {
  routine_id: string;
  routine_name: string;
  routine_status: RoutineStatus;
  status: 'pending' | 'active' | 'revoked' | 'expired' | string;
  granted_at?: string | null;
  expires_at?: string | null;
}

// ---------------------------------------------------------------- emergency / inbox / platform

export interface EmergencyRuleInfo {
  code: string;
  action: string;
  severity: string;
  relaxable: boolean;
  admin_only_ack: boolean;
  description: string;
}

export interface EmergencyState {
  rules: EmergencyRuleInfo[];
  tenant_switch: boolean;
  dr_switch: boolean;
  stopped_runs: number;
  can_stop: boolean;
}

export interface BulkStopResult {
  requested: number;
  stopped: number;
  skipped: number;
  not_found: number;
}

export interface FrontierInterventionItem {
  id: string;
  run_id: string;
  kind: string;
  question?: string | null;
  options?: string[] | null;
  suggested_default?: string | null;
  assignees?: string[] | null;
  sla_expires_at?: string | null;
  status: string;
  response?: Record<string, unknown> | null;
  responded_by?: string | null;
  created_at: string;
  run_status?: RunStatus | null;
  routine_id?: string | null;
  routine_name?: string | null;
}

export interface RunAttemptInfo {
  attempt: number;
  status: string;
  error_class?: string | null;
  backoff_seconds?: number | null;
  started_at?: string | null;
  ended_at?: string | null;
}

export interface FrontierPlatformSummary {
  tenants_with_activity: number;
  routines_by_status: Record<string, number>;
  runs_by_status: Record<string, number>;
  runs_window_hours: number;
  emergency_stopped: number;
  interventions_open: number;
  generated_at: string;
}
